/**
 * Full-viewport background effect mounted on document.body (layer 0).
 * Supports Silk (waves) and LiquidEther (fluid). UI lives in #root (layer 1).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import LiquidEther from './LiquidEther';
import Silk from './Silk';
import {
  ARCHON_BACKGROUND_EFFECTS,
  normalizeBackgroundEffect,
} from '../../theme/backgroundEffects';
import { getLiquidEtherTheme, getSilkTheme } from '../../theme/archonTheme';
import '../../styles/archonBackground.css';

const BG_HOST_ID = 'archon-bg-layer';

function getOrCreateHost() {
  if (typeof document === 'undefined') return null;
  let host = document.getElementById(BG_HOST_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = BG_HOST_ID;
    host.setAttribute('aria-hidden', 'true');
    const root = document.getElementById('root');
    if (root?.parentNode) {
      root.parentNode.insertBefore(host, root);
    } else {
      document.body.prepend(host);
    }
  }
  return host;
}

function renderEffect(effect, theme) {
  const resolved = normalizeBackgroundEffect(effect);

  if (resolved === ARCHON_BACKGROUND_EFFECTS.LIQUID_ETHER) {
    const config = getLiquidEtherTheme(theme);
    return (
      <LiquidEther
        key={`liquid-ether-${theme}`}
        className="archon-bg-layer__ether"
        colors={config.colors}
        mouseForce={config.mouseForce}
        cursorSize={config.cursorSize}
        resolution={config.resolution}
        autoDemo={config.autoDemo}
        autoSpeed={config.autoSpeed}
        autoIntensity={config.autoIntensity}
      />
    );
  }

  const config = getSilkTheme(theme);
  return (
    <div className="archon-bg-silk">
      <Silk
        key={`silk-${theme}`}
        speed={config.speed}
        scale={config.scale}
        color={config.color}
        noiseIntensity={config.noiseIntensity}
        rotation={config.rotation}
      />
    </div>
  );
}

export default function ArchonBackgroundLayer({ theme, effect }) {
  const [host, setHost] = useState(null);
  const resolvedEffect = normalizeBackgroundEffect(effect);

  const fallbackBg = useMemo(() => {
    if (resolvedEffect === ARCHON_BACKGROUND_EFFECTS.LIQUID_ETHER) {
      return getLiquidEtherTheme(theme).fallbackBg;
    }
    return getSilkTheme(theme).fallbackBg;
  }, [resolvedEffect, theme]);

  useEffect(() => {
    setHost(getOrCreateHost());
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.dataset.archonBg = theme;
    document.body.dataset.archonBgEffect = resolvedEffect;
    document.body.style.backgroundColor = fallbackBg;
    return () => {
      delete document.body.dataset.archonBg;
      delete document.body.dataset.archonBgEffect;
    };
  }, [theme, resolvedEffect, fallbackBg]);

  if (!host) return null;

  return createPortal(renderEffect(resolvedEffect, theme), host);
}
